import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { flushSync } from 'react-dom'

import { t } from '@ctcells/i18n'

import { FIT_INITIAL, applyFit, fitSteps, forgetWidths, nextFitMeasured } from './columnFit.ts'
import { cx } from './cx.ts'
import { EmptyState } from './EmptyState.tsx'
import { isPlainRowClick } from './rowClick.ts'
import { Skeleton } from './Skeleton.tsx'
import { StateNotice, type StateNoticeTone } from './StateNotice.tsx'
import {
  isSortable,
  nextSort,
  sortRows,
  type SortKind,
  type SortValue,
  type TableSort,
} from './tableSort.ts'
import { ValueRows } from './ValueRows.tsx'

import './table.css'

/**
 * 표 — 와이어프레임 `.tbl` 172행 · `.mtbl` 480행.
 *
 * 🔴 **행 클릭은 그 자리에서 아래로 펼친다.** 모달·팝업을 띄우지 않는다(§1-3 · §14-4).
 * 🔴 키보드로도 펼쳐진다. 첫 칸을 진짜 `button` 으로 감쌌다(§11-4-7 보강).
 * 🔴 펼침 상태는 **호출부가 쥔다**(제어 컴포넌트). 화면이 어느 행을 열었는지 알아야
 *    메뉴를 옮길 때 닫을 수 있다(§14-1 "다른 메뉴로 이동하면 상세는 자동으로 닫힌다").
 *
 * 오른쪽 «서랍»으로 상세를 여는 목록은 펼침 대신 `onRowClick` 을 쓴다 — 마우스·터치용
 * 보태기이고, 키보드는 여전히 첫 칸의 진짜 `button` 으로 간다(그 prop 주석에 관계를 적었다).
 */
export type Column<Row> = ColumnBase<Row> & ColumnSort<Row>

/**
 * **머리글 정렬**(eric 2026-09-30 「게시판 테이블의 컬럼 헤더 클릭하면.. 내림 차순 정렬.. 한번 더 클릭하면 오름 차순」).
 *
 * - `sort: 'text' | 'number' | 'time'` + `sortValue(row)` — 값 종류와 값 읽는 법. 🔴 **둘은 짝이다**(타입이 강제한다) —
 *   칸(`cell`)은 화면에 그리는 것이라 비교에 못 쓴다. 표기(`2026-09-30 13:05`)가 아니라 **원값**(ISO · 수)을 준다
 * - `sort: (a, b) => number` — 오름차순 기준 비교 함수(상태 차례처럼 값 종류로 안 되는 열)
 * - 안 주면 그 열은 정렬되지 않는다(머리글이 버튼이 아니다)
 *
 * 비교 규칙(값 없음은 방향 무관 맨 뒤 · 안정 정렬 · 시각은 시각으로)은 `tableSort.ts` 한 곳이다.
 */
type ColumnSort<Row> =
  | { sort?: undefined; sortValue?: undefined }
  | { sort: SortKind; sortValue: (row: Row) => SortValue }
  | { sort: (a: Row, b: Row) => number; sortValue?: undefined }

type ColumnBase<Row> = {
  key: string
  /** 열 제목(한글). 짧은 쪽을 쓴다(§1-1) */
  header: string
  cell: (row: Row) => ReactNode
  nowrap?: boolean | undefined
  /**
   * 끊을 데 없는 긴 값(랩웨어 경로 · 자원 id)을 **글자 사이에서 접는다.** `nowrap` 의 반대다.
   *
   * 🔴 겉모습이 아니라 **폭 계산**을 고치는 것이다. 공백 없는 값은 열의 min-content 가
   *    값 전체 길이가 되어, 표가 `width:100%` 여도 그 아래로 못 줄고 **카드 밖으로 넘친다**
   *    (2026-09-21 시약 표의 「자리」 열 `staining.reagent_bay.ambient.1`).
   */
  wrapAnywhere?: boolean | undefined
  align?: 'start' | 'end' | undefined
  /**
   * **열이 남는 폭을 얼마나 받는가**(eric 2026-09-30 「작업 ID와 결과 사이에 공간이 너무 많은데」).
   *
   * - `'fit'` — **내용 폭만** 쓴다. 한 줄로 선다(`nowrap` 을 겸한다). 식별자 · 수 · 시각 · 상태처럼 짧은 열
   * - `'fill'` — **남는 폭을 이 열이 받는다.** 표마다 하나만 준다
   * - 안 주면 종전 그대로(자동 배분)
   *
   * 🔴 까닭: 표는 `width:100%` 자동 배치라 브라우저가 남는 폭을 **열 내용 폭에 비례해** 나눈다.
   *    36자 UUID 열이 남는 폭을 제일 많이 받아 다음 열이 멀리 떨어졌다(열이 줄수록 더 벌어진다).
   * 🔴 **최소 폭은 바꾸지 않는다** — 열 맞춤(`columnFit.ts`)은 넘칠 때의 표 폭(= 최소 폭)을 재는데,
   *    `width:1%` · `100%` 는 남는 폭의 배분만 바꾸고 칸의 min-content 는 그대로다. 좁으면 종전처럼 넘치고 빠진다.
   */
  width?: 'fit' | 'fill' | undefined
  /**
   * **공간이 모자라면 빠지는 차례**(eric 2026-09-28 「목록의 일부 열을 공간이 모자라면 숨겨야지」).
   * 낮을수록 먼저 빠진다. **안 주면 늘 보인다** — 표 전체에 하나도 없으면 종전 그대로다.
   *
   * 🔴 재는 것은 **표가 놓인 상자의 폭**이다(서랍이 열리면 목록 상자만 좁아진다). 판정은 `columnFit.ts`.
   * 🔴 첫 열은 줘도 안 빠진다(행을 여는 키보드 경로가 그 칸이다). 상태 · 등급 열에는 주지 마라.
   * 🔴 **줄 때는 그 값을 서랍에서 볼 수 있어야 한다** — 목록에서 사라진 값이 갈 곳이 없어진다
   *    (`screens/listColumnPriority.ts` · `drawerNoRepeat.test.tsx`).
   * 빠진 열은 머리 칸과 본문 칸이 **같이** DOM 에서 빠진다(보조기술이 빈 칸을 읽지 않는다).
   */
  priority?: number | undefined
  /**
   * **줄인 꼴** — 폭이 모자라면 빼기 전에 이 칸으로 바꿔 그린다(eric 2026-09-28 「줄여」 · 작업 ID 앞 8자).
   * `compact.priority` 는 열 `priority` 와 같은 숫자 축이다(같으면 줄이기가 먼저). 넉넉하면 늘 원래 `cell` 이다.
   * 🔴 줄인 꼴도 전체 값을 잃지 않아야 한다 — 툴팁(`title`)과 보조기술 글자로 원값을 싣는다(`ShortId`).
   */
  compact?: { priority: number; cell: (row: Row) => ReactNode } | undefined
  /**
   * **내보내기(CSV) 글자** — 칸(`cell`)이 배지 · 아이콘처럼 글자가 아닌 것을 그릴 때만 준다(예: 심각도 배지 → 「경고」).
   * 안 주면 `sortValue` 를 쓰고(시각이면 화면과 같은 표기), 그것도 없으면 빈 칸이다(`screens/logs/logsCsv.ts`).
   */
  exportValue?: ((row: Row) => string) | undefined
}

/** 행의 결 — `rowTone` 이 돌려주는 값. 클래스 `a-tbl__row--tone-<결>` 하나로 나간다(`table.css`) */
export type RowTone = 'danger' | 'warn' | 'muted'

export type DataTableProps<Row> = {
  columns: readonly Column<Row>[]
  rows: readonly Row[]
  rowKey: (row: Row) => string
  /** 접근성 이름. 필수 */
  caption: string
  /** 주면 행이 펼쳐진다. 없으면 읽기 전용 표 */
  renderExpanded?: ((row: Row) => ReactNode) | undefined
  /** 지금 펼쳐진 행 키 */
  expandedKey?: string | null | undefined
  onToggleExpand?: ((key: string) => void) | undefined
  /** 강조할 행(치명 이벤트 등) — 왼쪽 굵은 띠 */
  emphasis?: ((row: Row) => boolean) | undefined
  /**
   * 행의 **결**(디자인팀 시안 2026-10-01 · `docs/design/DESIGN-CHANGES.md` §4 「알람 행」 · 「시료 미입력」).
   *   `danger` — 왼쪽 4px 위험색 띠(활성 오류 알람)
   *   `warn`   — 왼쪽 4px 주의색 띠(미확인 경고 알람 · 미입력 시료)
   *   `muted`  — 글자만 흐리게(해제된 알람)
   * 🔴 **색만으로 말하지 않는다** — 띠가 붙는 행은 같은 사실을 칸의 글자(심각도 · 상태 · 「미입력」 배지)가 이미 말한다.
   *    흑백 앱에서는 띠가 먹색 · 회색으로 서고 글자가 계속 가른다. 부르는 쪽이 **있는 값으로만** 판정한다 — 새 데이터를 만들지 않는다.
   */
  rowTone?: ((row: Row) => RowTone | undefined) | undefined
  /** 불러오는 중 — 스켈레톤 행을 그린다 */
  loading?: boolean | undefined
  /** 빈 상태 문구. 기본은 '없음' */
  emptyLabel?: string | undefined
  /**
   * **못 본 까닭**(권한 없음 · 조회 실패). 주면 빈 상태 «대신» 이것이 선다.
   *
   * 🔴 **`emptyLabel` 에 사유를 밀어 넣지 마라**(2026-09-22 실측). 화면 여럿이
   *    `emptyLabel={notice ?? t('state.empty.…')}` 로 둘을 한 칸에 겹쳐 놓고 있었고,
   *    그래서 viewer 의 「권한이 없습니다」와 검색 0건의 「시료 없음」이 **같은 회색 한 줄**이었다.
   *    「못 봤다」와 「0건」은 다른 사실이다 — 생김새로도 갈린다(`StateNotice`).
   * 🔴 사유가 있으면 **스켈레톤도 그리지 않는다.** 실패한 조회에 「불러오는 중」을 겹쳐
   *    보이면 기다리면 온다고 읽힌다.
   */
  notice?: string | undefined
  /** 사유의 성격. 통신 실패·5xx 는 `failed`, 권한·잠김은 기본값 */
  noticeTone?: StateNoticeTone | undefined
  /** 기본 'managed'(목록 화면). 'compact' 는 패널 안 조밀한 표 */
  density?: 'managed' | 'compact' | undefined
  /** 지금 «고른» 행(오른쪽 판이 보이는 것 등). 채움 + `aria-selected` — 색만으로 말하지 않는다 */
  selected?: ((row: Row) => boolean) | undefined
  /** 현재 행의 접근성 표지. 지정하지 않으면 기존 행 속성은 그대로다. */
  current?: ((row: Row) => boolean) | undefined
  /**
   * 행 **아무 칸이나** 눌러 여는 길(eric 2026-09-23 「해당 row 의 어떤 부분을 클릭해도
   * 상세 페이지가 보이게 해줘」). 오른쪽 서랍을 여는 목록들이 쓴다.
   *
   * 🔴 **마우스·터치용 «보태기»다. 키보드 경로가 아니다.** 첫 칸의 진짜 `button`(`RowLink` ·
   *    펼침 버튼)이 계속 그 일을 한다 — 여기 `<tr>` 에 `tabIndex`·`role="button"` 을 달지 마라.
   *    표의 시맨틱이 깨지고 스크린리더가 행마다 「버튼」을 읽는다(§11-4-7 의 답이 그 첫 칸 버튼이다).
   * 🔴 **행 안의 상호작용 요소가 우선한다.** 「요약 PDF」·「시료 CSV」·행 메뉴에서 시작한 클릭은
   *    행을 열지 않는다(eric 「그 버튼이 우선순위가 높고」). 판정은 `isPlainRowClick`.
   * 🔴 **이미 열린 행을 다시 눌러도 닫히지 않는다** — 닫기는 서랍의 × 다. 여는 쪽만 이 길이다
   *    (닫는 것은 호출부가 같은 id 를 다시 넘기는 것으로 끝난다 — 아무 일도 일어나지 않는다).
   * ⚠ `renderExpanded`(그 자리 펼침)와 함께 쓰지 않는다. 둘 다 오면 **펼침이 이긴다**.
   */
  onRowClick?: ((row: Row) => void) | undefined
  /**
   * **남는 폭을 모든 열이 똑같이 나눈다**(eric 2026-09-30 「레시피 부분의 뒤가 많이 비고.. 전반적으로 안맞아」).
   *
   * - `'even'` — 칸마다 내용 폭 + **같은 몫의 여백**. 열 사이 간격이 고르게 선다. 칸은 한 줄이다
   * - 안 주면 종전 그대로(열마다 `Column.width` · 자동 배분)
   *
   * 🔴 까닭: `fill` 열 하나가 남는 폭을 다 받으면 그 열 뒤만 크게 빈다(작업 목록 · 이력의 레시피). 자동 배분은
   *    내용 폭에 비례해 나눠 36자 UUID 열이 제일 많이 받는다. `even` 은 모든 칸을 `width:1%` 로 두어 브라우저가
   *    남는 폭을 **퍼센트 비율(= 똑같이)**로 나누게 한다 — 이 표에서는 `Column.width` 가 무시된다.
   * 🔴 **최소 폭은 그대로다**(퍼센트는 남는 폭의 배분만 바꾼다) — 열 맞춤(`columnFit.ts`)이 재는 넘칠 때의 폭은 같다.
   * ⚠ 칸을 한 줄로 세운다 — 접히는 칸에 `1%` 를 주면 남는 폭이 있어도 내용이 접힌다. 접혀야 하는 표에는 쓰지 마라.
   */
  columnWidths?: 'even' | undefined
  /**
   * **빠지거나 줄어든 열의 값을 그 행의 펼침에 싣는다**(eric 2026-09-30 「1/3 정도만 drawer 형태로」).
   *
   * 서랍이 좁아지면 서랍 «안»의 표도 열이 빠진다(`priority`). 목록의 열은 서랍으로 가면 되지만 서랍 안의 표는
   * 갈 곳이 없다 — 그래서 **행을 펼치면 빠진 열(머리 · 값)과 줄인 열의 원값이 선다.** 넉넉해서 아무것도 안 빠지면
   * 펼칠 것이 없어 행이 펼침 단추가 되지 않는다(`renderExpanded` 가 따로 있으면 종전 그대로 펼쳐진다).
   *
   * 🔴 펼침 상태는 여전히 **호출부가 쥔다** — `expandedKey` · `onToggleExpand` 를 같이 넘긴다(없으면 펼치지 않는다).
   * 🔴 펼침 안의 값은 **글자 사이에서 접힌다** — 긴 식별자가 펼친 칸을 넓혀 표를 다시 넘치게 하면
   *    열 맞춤이 또 한 열을 뺀다(재는 것은 표 전체 폭이다).
   */
  revealHidden?: boolean | undefined
  /**
   * 표 아래 「N건」의 수. **안 주면 행 수다.** 행 중 일부가 «자리»일 때 쓴다 — 작업 상세 시료 표는 배정 없는
   * 웰까지 줄을 세우지만 건수는 배정된 시료 수다(eric 2026-09-30). ⚠ 행이 0개면 건수 줄 자체가 안 선다(종전 그대로).
   */
  rowCount?: number | undefined
  /** 표 아래 「N건」 줄을 세우지 않는다(템플릿 목록 · eric 2026-10-13 「2건이라는 건 빼줘」) */
  hideCount?: boolean | undefined
  /**
   * **처음 정렬**(화면이 정한다 — 알람 = 발생 시각 내림차순 등). 안 주면 받은 차례 그대로이고, 머리글을
   * 눌러야 정렬된다. 상태는 표가 쥔다(아래 `sort` 를 안 넘길 때).
   */
  defaultSort?: TableSort | undefined
  /**
   * 정렬 상태를 **부르는 쪽이 쥘 때**(제어). 넘기면 표는 이 값만 따르고, 머리글을 누르면 `onSortChange` 로
   * 다음 상태를 알린다. `null` = 정렬 없음(받은 차례).
   */
  sort?: TableSort | null | undefined
  /** 머리글을 눌렀을 때 — 다음 상태(새 열은 내림차순 · 같은 열은 방향만 뒤집기) */
  onSortChange?: ((sort: TableSort) => void) | undefined
  /**
   * 🔴 **줄 차례를 부르는 쪽(서버)이 이미 맞춰 보냈다** — 표는 머리글 표시(▼▲ · `aria-sort`)만 하고 줄을
   * 다시 세우지 않는다. 서버 정렬 인자가 있는 쪽 나눔 표가 쓴다(쪽 안에서 다시 세우면 쪽 경계가 거짓말이 된다).
   */
  manualSort?: boolean | undefined
}

const SKELETON_ROWS = 3

export function DataTable<Row>({
  columns: allColumns,
  rows,
  rowKey,
  caption,
  renderExpanded,
  expandedKey,
  onToggleExpand,
  emphasis,
  rowTone,
  loading = false,
  emptyLabel,
  notice,
  noticeTone,
  density = 'managed',
  selected,
  current,
  onRowClick,
  columnWidths,
  revealHidden = false,
  rowCount,
  hideCount = false,
  defaultSort,
  sort: sortProp,
  onSortChange,
  manualSort = false,
}: DataTableProps<Row>) {
  const fitBox = useRef<HTMLDivElement>(null)
  /*
   * 🔴 열 맞춤에는 **받은 배열 그대로**를 넘긴다 — 정렬한 배열은 그릴 때마다 새것이라, 넘기면 「내용이 바뀌었다」로
   *    읽혀 잰 폭을 매번 잊는다. 차례는 폭을 안 바꾼다.
   */
  const fit = useColumnFit(allColumns, rows, fitBox)
  const [ownSort, setOwnSort] = useState<TableSort | null>(defaultSort ?? null)
  const sortState = sortProp === undefined ? ownSort : sortProp
  const onSortClick = (key: string) => {
    const next = nextSort(sortState, key)
    if (sortProp === undefined) setOwnSort(next)
    onSortChange?.(next)
  }
  /* 🔴 빠진 열로도 정렬은 선다 — 비교는 원래 열 목록(`allColumns`)으로 한다 */
  const shownRows = manualSort ? rows : sortRows(rows, allColumns, sortState)
  const columns = fit.columns
  /* 🔴 원래 차례대로 — 빠진 열과 줄인 열(원값) */
  const revealed = revealHidden
    ? allColumns.filter(
        (column) => fit.hiddenKeys.includes(column.key) || fit.compactKeys.includes(column.key),
      )
    : []
  const expandable =
    onToggleExpand !== undefined && (renderExpanded !== undefined || revealed.length > 0)
  /* 마우스가 행 위에서 손가락이 되는 조건. 펼침 표와 서랍 목록이 같은 생김새를 쓴다 */
  const rowClickable = expandable || onRowClick !== undefined
  /* 못 본 자리에는 기다림을 그리지 않는다 — 스켈레톤은 「곧 온다」는 말이다 */
  const pending = loading && notice === undefined

  /*
   * 🔴 **머리(`th`)와 셀(`td`)에 «같은» 정렬이 붙는다**(2026-09-22 · eric 「보고서는 값이 비어
   *    있는데 왜 그런 거야?」). 종전에는 `a-tbl__cell--end` 가 셀에만 붙어 **머리는 왼쪽,
   *    값은 오른쪽 끝**이었다 — 이력의 「보고서」 머리 아래가 **빈 것처럼 보였다**(값은 저 멀리
   *    오른쪽에 있었는데도). 열 하나가 두 정렬을 쓰면 사람이 머리와 값을 못 잇는다.
   * ⚠ `align: 'end'` 를 쓰는 다른 표도 같이 바르게 선다 — 의도한 변화다.
   */
  const cellClass = (column: Column<Row>): string =>
    cx(
      column.nowrap === true && 'a-tbl__cell--nowrap',
      column.wrapAnywhere === true && 'a-tbl__cell--anywhere',
      column.align === 'end' && 'a-tbl__cell--end',
      column.width === 'fit' && 'a-tbl__cell--fit',
      column.width === 'fill' && 'a-tbl__cell--fill',
    )

  const table = (
    <table
      className={cx(
        'a-tbl',
        density === 'managed' && 'a-tbl--managed',
        columnWidths === 'even' && 'a-tbl--even',
      )}
    >
      <caption className="a-sr">{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => {
            if (!isSortable(column)) {
              return (
                <th className={cellClass(column)} key={column.key} scope="col">
                  {column.header}
                </th>
              )
            }
            const direction = sortState?.key === column.key ? sortState.direction : undefined
            /*
             * 🔴 **머리글이 진짜 `button` 이다**(키보드 · 스크린리더). 방향은 `th` 의 `aria-sort` 가 말하고,
             *    ▼▲ 글리프는 CSS 가 그린다(`data-sort` · `table.css`) — 글자로 넣으면 머리글 이름이 「발생 시각▼」가 된다.
             * ⚠ `aria-sort` 는 지금 정렬 중인 열 하나에만 단다(ARIA 권고).
             */
            return (
              <th
                aria-sort={
                  direction === undefined
                    ? undefined
                    : direction === 'desc'
                      ? 'descending'
                      : 'ascending'
                }
                className={cellClass(column)}
                key={column.key}
                scope="col"
              >
                <button
                  className="a-tbl__sort"
                  data-sort={direction}
                  onClick={() => onSortClick(column.key)}
                  type="button"
                >
                  {column.header}
                </button>
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {pending ? (
          Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <tr key={`skeleton-${String(i)}`}>
              {columns.map((column) => (
                <td key={column.key}>
                  <Skeleton />
                </td>
              ))}
            </tr>
          ))
        ) : notice !== undefined ? (
          <tr className="a-tbl__empty">
            <td colSpan={columns.length}>
              <StateNotice
                reason={notice}
                {...(noticeTone === undefined ? {} : { tone: noticeTone })}
              />
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr className="a-tbl__empty">
            <td colSpan={columns.length}>
              <EmptyState label={emptyLabel ?? t('state.empty.generic')} />
            </td>
          </tr>
        ) : (
          shownRows.map((row) => {
            const key = rowKey(row)
            const open = expandable && expandedKey === key
            const panelId = `${key}-panel`
            const tone = rowTone?.(row)

            return (
              <RowGroup key={key}>
                <tr
                  aria-current={current?.(row) === true ? true : undefined}
                  aria-selected={selected?.(row) === true ? true : undefined}
                  className={cx(
                    emphasis?.(row) === true && 'a-tbl__row--emphasis',
                    tone !== undefined && `a-tbl__row--tone-${tone}`,
                    rowClickable && 'a-tbl__row--clickable',
                    open && 'a-tbl__row--open',
                    selected?.(row) === true && 'a-tbl__row--selected',
                  )}
                  onClick={
                    rowClickable
                      ? (event) => {
                          // 행 안의 단추·링크·입력이 먼저다. 글자를 끌어 고른 뒤의 클릭도 아니다
                          if (!isPlainRowClick(event)) return
                          if (expandable) onToggleExpand(key)
                          else onRowClick?.(row)
                        }
                      : undefined
                  }
                >
                  {columns.map((column, index) => (
                    <td className={cellClass(column)} key={column.key}>
                      {expandable && index === 0 ? (
                        <button
                          aria-controls={open ? panelId : undefined}
                          aria-expanded={open}
                          className="a-tbl__rowbtn"
                          onClick={() => onToggleExpand(key)}
                          type="button"
                        >
                          {column.cell(row)}
                        </button>
                      ) : (
                        column.cell(row)
                      )}
                    </td>
                  ))}
                </tr>
                {open ? (
                  <tr className="a-tbl__expand">
                    <td colSpan={columns.length} id={panelId}>
                      {revealed.length === 0 ? null : (
                        <div className="a-tbl__reveal">
                          <ValueRows
                            rows={revealed.map((column) => ({
                              key: column.key,
                              label: column.header,
                              value: column.cell(row),
                            }))}
                          />
                        </div>
                      )}
                      {renderExpanded?.(row)}
                    </td>
                  </tr>
                ) : null}
              </RowGroup>
            )
          })
        )}
      </tbody>
    </table>
  )

  /*
   * 🔴 **표의 «마무리» 줄**(2026-09-22). 목록이 한두 줄일 때 표가 허공에서 끊겨 보여서
   *    「여기까지가 전부인가」가 안 읽혔다(eric 지적 — 패널 아래 500px 가 비는 것과 같은 사정).
   * 🔴 **응답에 있는 값만 쓴다** — 지금 그린 행 수다. 서버 전체 건수(`total`)가 아니다.
   *    합계·집계를 화면에서 만들어 내지 마라.
   * 🔴 **`<tfoot><tr>` 이 아니라 표 «밖»이다.** 표 안에 두면 그 줄이 `role="row"` 로
   *    잡혀서, 「행」을 세는 시험·보조기술이 **데이터 행이 하나 더 있다**고 읽는다
   *    (2026-09-22 시험이 바로 잡았다). 건수는 표의 행이 아니다.
   * ⚠ 관리형(목록 화면)에만 붙인다. 패널 안 조밀한 표(`compact`)는 그 자체가 한 조각이라
   *   줄이 하나 더 붙으면 카드가 길어지기만 한다.
   */
  /*
   * 빠지거나 줄 수 있는 열이 있는 표만 상자로 감싼다 — 그 상자의 폭을 잰다. 없는 표는 DOM 이 종전 그대로다.
   * `data-hidden-columns` · `data-compact-columns` 는 지금 빠진 · 줄인 열 키(계측 · 시험용). 사람에게 보이는 것은 아니다
   */
  const body = fit.hideable ? (
    <div
      className="a-tbl__fit"
      data-hidden-columns={fit.hiddenKeys.length === 0 ? undefined : fit.hiddenKeys.join(' ')}
      data-compact-columns={fit.compactKeys.length === 0 ? undefined : fit.compactKeys.join(' ')}
      ref={fitBox}
    >
      {table}
    </div>
  ) : (
    table
  )

  if (density !== 'managed' || pending || notice !== undefined || rows.length === 0 || hideCount)
    return body

  return (
    <>
      {body}
      <p className="a-tbl__count">{t('table.rowCount', { count: rowCount ?? rows.length })}</p>
    </>
  )
}

/**
 * 열 맞춤 훅 — 표가 놓인 상자의 폭을 재서 `columnFit.ts` 에 묻는다.
 *
 * - 매 그리기 뒤(레이아웃 단계)에 한 번 잰다. 넘치면 한 열 빼고 다시 그리고, 또 잰다 — 그리기 «전»에
 *   끝나므로 넘친 표가 화면에 한 번도 안 보인다
 * - 상자 폭이 바뀌면(서랍 열고 닫기 · 창 크기) `ResizeObserver` 로 다시 잰다. 그 길만 `flushSync` 로
 *   바로 그린다 — 안 그러면 한 프레임 가로 스크롤이 보인다
 * - 행 · 열이 바뀌면 잰 폭을 잊는다(빠진 열이 되살아날 수 있는지 다시 본다)
 * - `ResizeObserver` 가 없는 곳(jsdom)에서는 효과만 돌고, 폭이 0 이라 아무것도 안 빠진다
 */
function useColumnFit<Row>(
  columns: readonly Column<Row>[],
  rows: readonly Row[],
  ref: RefObject<HTMLDivElement | null>,
) {
  /* 🔴 `hideable` 은 이제 «단계 수»다 — 줄이기와 빼기가 한 줄로 선다(`fitSteps`) */
  const fitColumns = columns.map((column) => ({
    key: column.key,
    priority: column.priority,
    compactPriority: column.compact?.priority,
  }))
  const hideable = fitSteps(fitColumns).length
  const [state, setState] = useState(FIT_INITIAL)
  /*
   * 🔴 **지금 화면에 그려진 그림의 `hidden`** — 재는 값은 그 그림의 것이다. 레이아웃 효과와 `ResizeObserver` 가
   *    한 그림을 두 번 재도 둘째는 낡은 값으로 버려진다(`nextFitMeasured` 머리말 · 쵸단 실측 2026-09-30).
   */
  const drawnHidden = useRef(0)
  const content = useRef<{ rows: readonly Row[]; signature: string } | null>(null)
  const signature = fitColumns
    .map((column) => `${column.key}:${String(column.priority)}:${String(column.compactPriority)}`)
    .join('|')

  const measure = useCallback(() => {
    const box = ref.current
    const table = box?.querySelector(':scope > table')
    if (box == null || !(table instanceof HTMLElement)) return
    /*
     * 소수점까지 잰다 — 정수로 반올림한 `offsetWidth` 로 재면 0.x px 넘친 표를 「들어간다」로 읽어
     * 가로 스크롤 막대가 남았다(1255×742 알람 실측 · 표 465 / 상자 464).
     */
    const available = box.getBoundingClientRect().width
    const tableWidth = table.getBoundingClientRect().width
    const measuredHidden = drawnHidden.current
    setState((prev) => nextFitMeasured(prev, measuredHidden, { available, tableWidth, hideable }))
  }, [hideable, ref])

  /*
   * 그린 뒤마다 잰다 — 열이 하나 빠지면(`state`) 다시 그려지고 여기가 또 돈다. 바뀔 게 없으면 `nextFit` 이
   * 같은 객체를 돌려줘 멈춘다(끝없이 돌지 않는다 — `columnFit.test.ts` 의 `settle` 이 그 수렴을 본다)
   */
  useLayoutEffect(() => {
    drawnHidden.current = state.hidden
    if (hideable === 0) return
    const seen = content.current
    if (seen === null || seen.rows !== rows || seen.signature !== signature) {
      content.current = { rows, signature }
      if (seen !== null) setState(forgetWidths)
    }
    measure()
  }, [hideable, measure, rows, signature, state])

  useLayoutEffect(() => {
    const box = ref.current
    if (hideable === 0 || box == null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => flushSync(measure))
    observer.observe(box)
    return () => observer.disconnect()
  }, [hideable, measure, ref])

  const applied = Math.min(state.hidden, hideable)
  const fit = applyFit(fitColumns, applied)
  const shown = new Set(fit.columns.map((column) => column.key))
  const compacted = new Set(fit.compacted)
  return {
    hideable: hideable > 0,
    columns:
      hideable === 0
        ? columns
        : columns
            .filter((column) => shown.has(column.key))
            .map((column) =>
              compacted.has(column.key) && column.compact !== undefined
                ? { ...column, cell: column.compact.cell }
                : column,
            ),
    hiddenKeys: fit.hidden,
    compactKeys: fit.compacted,
  }
}

/** `<tbody>` 안에서 행 두 개를 한 키로 묶는다. Fragment 만으로는 key 를 못 준다 */
function RowGroup({ children }: { children: ReactNode }) {
  return <>{children}</>
}
